import React, { useEffect, useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getFiled } from "../../services/backend/CorrespondenceService";
import { useSelector } from "react-redux";
import { firestore } from "../../config/ConfigFirebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { describeEvent } from "../pqrsd/pqrsdEventDescriptions";
import {
  CANCELLATION_EVENT_TYPES,
  publicTimelineEvents,
  responseCancellationEvent,
  rootCancellationEvent,
  wasFinalizedBeforeCancelling,
} from "./timelineCancellation";
import { formatTimelineDate } from "./formatTimelineDate";

import { pqrsdText } from "../pqrsd/pqrsdLabel";
// Dates read "lunes, 14 de septiembre de 2026, 7:03 p. m." (see formatTimelineDate).
const formatSpanishDate = formatTimelineDate;

// Helper: Map English event types to Spanish (for display)
const mapEventTypeToSpanish = (englishType) => {
  const mapping = {
    'FILED': 'RADICADO',
    'ASSIGNED': 'ASIGNADO',
    'RESPONSE_CREATED': 'RESPUESTA_CREADA',
    'FORWARDED': 'REENVIADO',
    'RESPONSE_FILED': 'RESPUESTA_RADICADA',
    'FINALIZED': 'FINALIZADO',
    'RESPONSE_FLOW_STARTED': 'APROBACION_INICIADA',
    'RESPONSE_FLOW_STEP_DONE': 'APROBACION_PASO',
    'RESPONSE_FLOW_REJECTED': 'APROBACION_DEVUELTA',
    'RESPONSE_FLOW_DONE': 'APROBACION_COMPLETA',
    // New PQRSD multi-response / lifecycle events.
    'PQRSD_ASSIGNED': 'PQRSD_ASIGNADA',
    'PQRSD_RETURNED': 'PQRSD_DEVUELTA',
    'PQRSD_FORWARDED': 'PQRSD_REENVIADA',
    'PQRSD_REASSIGNED': 'PQRSD_REASIGNADA',
    'PQRSD_CITIZEN_CLOSED': 'PQRSD_CERRADA_CIUDADANO',
    'PQRSD_CITIZEN_REOPENED': 'PQRSD_REABIERTA',
    'PQRSD_SLA_RESET': 'PQRSD_PLAZO_REINICIADO',
    // Requerimiento de información al ciudadano (CPACA art. 17).
    'PQRSD_INFO_REQUESTED': 'PQRSD_INFO_REQUERIDA',
    'PQRSD_INFO_REPLIED': 'PQRSD_INFO_RESPONDIDA',
    'PQRSD_INFO_REQUEST_WITHDRAWN': 'PQRSD_INFO_RETIRADA',
    'PQRSD_INFO_EXTENSION_GRANTED': 'PQRSD_PRORROGA_CONCEDIDA',
    'PQRSD_INFO_EXPIRED': 'PQRSD_INFO_VENCIDA',
    'PQRSD_SLA_PAUSED': 'PQRSD_PLAZO_SUSPENDIDO',
    'PQRSD_SLA_RESUMED': 'PQRSD_PLAZO_REANUDADO',
    'PQRSD_DESISTIMIENTO_DECREED': 'PQRSD_DESISTIMIENTO_DECRETADO',
    // Recurso de reposición against the desistimiento.
    'PQRSD_APPEAL_FILED': 'PQRSD_RECURSO_INTERPUESTO',
    'PQRSD_APPEAL_GRANTED': 'PQRSD_RECURSO_REPUESTO',
    'PQRSD_APPEAL_DENIED': 'PQRSD_RECURSO_CONFIRMADO',
    'PQRSD_APPEAL_REMINDER_SENT': 'PQRSD_RECURSO_RECORDATORIO',
    'PQRSD_DESISTIMIENTO_FINAL': 'PQRSD_DESISTIMIENTO_EN_FIRME',
    'RESPONSE_FILED_INTERIM': 'RESPUESTA_INTERINA',
    'RESPONSE_FILED_FINAL': 'RESPUESTA_FINAL',
    'EMAIL_SENT': 'NOTIFICACION_ENVIADA',
    'PQRSD_INTAKE_VENTANILLA': 'PQRSD_RADICADA_VENTANILLA',
    'PQRSD_AI_CLASSIFIED': 'PQRSD_CLASIFICADA_IA',
    'PQRSD_AI_ROUTED': 'PQRSD_ENRUTADA_IA',
    'PQRSD_AI_NEEDS_REVIEW': 'PQRSD_REVISION_MANUAL',
    'FILED_CANCELLED': 'ANULADO',
    'PQRSD_CANCELLED': 'PQRSD_ANULADA',
    'RESPONSE_CANCELLED': 'RESPUESTA_ANULADA',
  };
  return mapping[englishType] || englishType;
};

// Helper: Normalize event to common structure (handles both English and Spanish schemas)
const normalizeEvent = (event) => {
  // If already normalized (has both), return as is
  if (event.eventType && event.tipoEvento) {
    return event;
  }
  
  // Convert from English schema to normalized
  if (event.eventType) {
    return {
      ...event,
      tipoEvento: mapEventTypeToSpanish(event.eventType),
      fecha: event.at || event.fecha,
      porUserId: event.byUserId || event.porUserId,
      porNombre: event.byName || event.porNombre,
      paraUserId: event.toUserId || event.paraUserId,
      paraNombre: event.toName || event.paraNombre,
      documentoId: event.documentId || event.documentoId,
      documentoNombre: event.documentName || event.documentoNombre,
      // Generate eventId if missing (for legacy events)
      eventId: event.eventId || generateEventIdFromEvent(event)
    };
  }
  
  // Convert from Spanish schema to normalized (legacy)
  return {
    ...event,
    eventType: mapEventTypeToEnglish(event.tipoEvento),
    at: event.fecha || event.at,
    byUserId: event.porUserId || event.byUserId,
    byName: event.porNombre || event.byName,
    toUserId: event.paraUserId || event.toUserId,
    toName: event.paraNombre || event.toName,
    documentId: event.documentoId || event.documentId,
    documentName: event.documentoNombre || event.documentName,
    // Generate eventId if missing (for legacy events)
    eventId: event.eventId || generateEventIdFromEvent(event)
  };
};

// Helper: Map Spanish event types to English (for storage/processing)
const mapEventTypeToEnglish = (spanishType) => {
  const mapping = {
    'RADICADO': 'FILED',
    'ASIGNADO': 'ASSIGNED',
    'RESPUESTA_CREADA': 'RESPONSE_CREATED',
    'REENVIADO': 'FORWARDED',
    'RESPUESTA_RADICADA': 'RESPONSE_FILED',
    'FINALIZADO': 'FINALIZED',
    'APROBACION_INICIADA': 'RESPONSE_FLOW_STARTED',
    'APROBACION_PASO': 'RESPONSE_FLOW_STEP_DONE',
    'APROBACION_DEVUELTA': 'RESPONSE_FLOW_REJECTED',
    'APROBACION_COMPLETA': 'RESPONSE_FLOW_DONE',
    'PQRSD_DEVUELTA': 'PQRSD_RETURNED',
    'PQRSD_REENVIADA': 'PQRSD_FORWARDED',
    'PQRSD_REASIGNADA': 'PQRSD_REASSIGNED',
    'PQRSD_CERRADA_CIUDADANO': 'PQRSD_CITIZEN_CLOSED',
    'PQRSD_REABIERTA': 'PQRSD_CITIZEN_REOPENED',
    'PQRSD_PLAZO_REINICIADO': 'PQRSD_SLA_RESET',
    'PQRSD_INFO_REQUERIDA': 'PQRSD_INFO_REQUESTED',
    'PQRSD_INFO_RESPONDIDA': 'PQRSD_INFO_REPLIED',
    'PQRSD_INFO_RETIRADA': 'PQRSD_INFO_REQUEST_WITHDRAWN',
    'PQRSD_PRORROGA_CONCEDIDA': 'PQRSD_INFO_EXTENSION_GRANTED',
    'PQRSD_INFO_VENCIDA': 'PQRSD_INFO_EXPIRED',
    'PQRSD_PLAZO_SUSPENDIDO': 'PQRSD_SLA_PAUSED',
    'PQRSD_PLAZO_REANUDADO': 'PQRSD_SLA_RESUMED',
    'PQRSD_DESISTIMIENTO_DECRETADO': 'PQRSD_DESISTIMIENTO_DECREED',
    'PQRSD_RECURSO_INTERPUESTO': 'PQRSD_APPEAL_FILED',
    'PQRSD_RECURSO_REPUESTO': 'PQRSD_APPEAL_GRANTED',
    'PQRSD_RECURSO_CONFIRMADO': 'PQRSD_APPEAL_DENIED',
    'PQRSD_RECURSO_RECORDATORIO': 'PQRSD_APPEAL_REMINDER_SENT',
    'PQRSD_DESISTIMIENTO_EN_FIRME': 'PQRSD_DESISTIMIENTO_FINAL',
    'RESPUESTA_INTERINA': 'RESPONSE_FILED_INTERIM',
    'RESPUESTA_FINAL': 'RESPONSE_FILED_FINAL',
    'NOTIFICACION_ENVIADA': 'EMAIL_SENT',
    'PQRSD_RADICADA_VENTANILLA': 'PQRSD_INTAKE_VENTANILLA',
    'PQRSD_CLASIFICADA_IA': 'PQRSD_AI_CLASSIFIED',
    'PQRSD_ENRUTADA_IA': 'PQRSD_AI_ROUTED',
    'PQRSD_REVISION_MANUAL': 'PQRSD_AI_NEEDS_REVIEW',
    'ANULADO': 'FILED_CANCELLED',
    'PQRSD_ANULADA': 'PQRSD_CANCELLED',
    'RESPUESTA_ANULADA': 'RESPONSE_CANCELLED',
  };
  return mapping[spanishType] || spanishType;
};

// Helper: Generate deterministic eventId from event (for legacy events)
const generateEventIdFromEvent = (event) => {
  const eventType = event.eventType || mapEventTypeToEnglish(event.tipoEvento) || 'UNKNOWN';
  const documentId = event.documentId || event.documentoId || 'none';
  const toUserId = event.toUserId || event.paraUserId || 'none';
  const at = event.at || event.fecha;
  let atMillis;
  if (at?.toMillis) {
    atMillis = at.toMillis();
  } else if (at?.toDate) {
    atMillis = at.toDate().getTime();
  } else if (at) {
    atMillis = new Date(at).getTime();
  } else {
    atMillis = Date.now();
  }
  return `${eventType}:${documentId}:${toUserId}:${atMillis}`;
};

// Outgoing docs that are chained to a radicado but are not answers to it.
const KIND_LABEL = {
  info_request: "Requerimiento de información",
  desistimiento: "Acto de desistimiento",
  appeal_decision: "Decisión del recurso de reposición",
};

const getEventLabel = (evento) => {
  // Use tipoEvento (Spanish) for display
  const tipoEvento = evento.tipoEvento || mapEventTypeToSpanish(evento.eventType);
  switch (tipoEvento) {
    case "RADICADO":
      return "Radicado";
    case "ASIGNADO":
      return `Asignado a ${evento.paraNombre || evento.toName || ""}`;
    case "RESPUESTA_CREADA":
      return KIND_LABEL[evento.responseKind]
        ? `${KIND_LABEL[evento.responseKind]} elaborado`
        : "Respuesta creada";
    case "REENVIADO":
      return `Reenviado a ${evento.paraNombre || evento.toName || ""}`;
    case "RESPUESTA_RADICADA":
      return KIND_LABEL[evento.responseKind]
        ? `${KIND_LABEL[evento.responseKind]} radicado`
        : "Respuesta radicada";
    case "FINALIZADO":
      return "Finalizado";
    case "APROBACION_INICIADA":
      return "Flujo de aprobación iniciado";
    case "APROBACION_PASO":
      return `Paso de aprobación firmado${evento.documentName ? ` (${evento.documentName})` : ""}`;
    case "APROBACION_DEVUELTA":
      return `Respuesta devuelta${evento.documentName ? ` en ${evento.documentName}` : ""}`;
    case "APROBACION_COMPLETA":
      return "Respuesta aprobada y radicada";
    case "PQRSD_RADICADA_VENTANILLA":
      return `Radicado por ventanilla${(evento.porNombre || evento.byName) ? ` por ${evento.porNombre || evento.byName}` : ""}`;
    case "PQRSD_DEVUELTA":
      return `Devuelta por operario${evento.reason ? ` — ${evento.reason}` : ""}`;
    case "PQRSD_REENVIADA":
      return `Reenviada a ${evento.toUserName || evento.paraNombre || ""}`;
    case "PQRSD_REASIGNADA":
      return `Reasignada a ${evento.toUserName || evento.paraNombre || ""}`;
    case "PQRSD_CERRADA_CIUDADANO":
      return "Cerrada por el ciudadano";
    case "PQRSD_REABIERTA":
      return `Reabierta por el ciudadano${evento.reason ? ` — ${evento.reason}` : ""}`;
    case "PQRSD_PLAZO_REINICIADO":
      return `Plazo de respuesta reiniciado${evento.trigger ? ` (${evento.trigger})` : ""}`;
    case "PQRSD_INFO_REQUERIDA":
      return `Requerimiento de información al ciudadano${evento.requerimientoFiledId ? ` (${evento.requerimientoFiledId})` : ""}`;
    case "PQRSD_INFO_RESPONDIDA":
      return "El ciudadano completó el requerimiento de información";
    case "PQRSD_INFO_RETIRADA":
      return `Requerimiento de información retirado${evento.reason ? ` — ${evento.reason}` : ""}`;
    case "PQRSD_PRORROGA_CONCEDIDA":
      return "Prórroga concedida al ciudadano";
    case "PQRSD_INFO_VENCIDA":
      return "Venció el plazo del ciudadano para completar la petición";
    case "PQRSD_PLAZO_SUSPENDIDO":
      return "Plazo de respuesta suspendido (requerimiento de información)";
    case "PQRSD_PLAZO_REANUDADO":
      return "Plazo de respuesta reanudado";
    case "PQRSD_DESISTIMIENTO_DECRETADO":
      return `Desistimiento tácito decretado${evento.actoFiledId ? ` (${evento.actoFiledId})` : ""}`;
    case "RESPUESTA_INTERINA":
      return `Respuesta interina publicada${evento.responseFiledId ? ` (${evento.responseFiledId})` : ""}`;
    case "RESPUESTA_FINAL":
      return `Respuesta final publicada${evento.responseFiledId ? ` (${evento.responseFiledId})` : ""}`;
    case "NOTIFICACION_ENVIADA":
    case "PQRSD_RECURSO_INTERPUESTO":
    case "PQRSD_RECURSO_REPUESTO":
    case "PQRSD_RECURSO_CONFIRMADO":
    case "PQRSD_RECURSO_RECORDATORIO":
    case "PQRSD_DESISTIMIENTO_EN_FIRME":
      return describeEvent({ ...evento, eventType: mapEventTypeToEnglish(tipoEvento) });
    case "PQRSD_CLASIFICADA_IA":
      return "Clasificada por IA";
    case "PQRSD_ENRUTADA_IA":
      return `Enrutada por IA${evento.groupName ? ` a ${evento.groupName}` : ""}`;
    case "PQRSD_REVISION_MANUAL":
      return `La IA no pudo clasificarla${evento.reason ? ` — ${evento.reason}` : ""}`;
    case "ANULADO":
      return "Radicado anulado";
    case "PQRSD_ANULADA":
      return pqrsdText("PQRSD anulada");
    case "RESPUESTA_ANULADA":
      return `${KIND_LABEL[evento.responseKind] || "Respuesta"} ${
        ["info_request", "desistimiento"].includes(evento.responseKind) ? "anulado" : "anulada"
      }${evento.responseFiledId ? ` (${evento.responseFiledId})` : ""}`;
    default:
      return tipoEvento || "Evento";
  }
};

export default function RadicadoTimeline({ 
  rootRadicadoId, 
  currentRadicadoId, 
  currentRadicadoReferenceId,
  isResponseView = false,
  originalRadicadoId,
  originalRadicadoReferenceId,
  // The radicado as the page already has it. Without a signed-in user the
  // timeline can't be loaded, so it is built from this instead.
  publicFiled,
}) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [originalRadicadoRef, setOriginalRadicadoRef] = useState(null);
  const token = useSelector((state) => state.user.value?.token);
  const navigate = useNavigate();

  // Load original radicado reference if viewing a response
  useEffect(() => {
    if (isResponseView && originalRadicadoId && token && !originalRadicadoRef) {
      (async () => {
        try {
          const originalData = await getFiled(token, originalRadicadoId);
          if (originalData?.referenceId) {
            setOriginalRadicadoRef(originalData.referenceId);
          }
        } catch (error) {
          console.error("Error loading original radicado:", error);
        }
      })();
    }
  }, [isResponseView, originalRadicadoId, token, originalRadicadoRef]);

  useEffect(() => {
    if (!token && publicFiled) {
      setTimeline(publicTimelineEvents(publicFiled));
      setLoading(false);
      return;
    }
    if (rootRadicadoId && token) {
      (async () => {
        setLoading(true);
        try {
          const rootData = await getFiled(token, rootRadicadoId);
          const allEvents = [];
          
          // Read events from events[] array (preferred) or eventos[] array (backward compatible)
          const rootEvents = (rootData?.events?.length ? rootData.events : (rootData?.eventos ?? []));
          
          if (rootEvents && Array.isArray(rootEvents) && rootEvents.length > 0) {
            // Normalize all events to common structure (handles both English and Spanish schemas)
            const processedEvents = rootEvents.map(evento => {
              const normalized = normalizeEvent(evento);
              // If this is a FILED/RADICADO event and we're viewing a response, make it clickable
              const eventType = normalized.eventType || mapEventTypeToEnglish(normalized.tipoEvento);
              if (eventType === "FILED" && isResponseView) {
                return {
                  ...normalized,
                  isClickable: true,
                  originalRadicadoReferenceId: rootData.referenceId || originalRadicadoRef,
                  originalRadicadoId: rootData.filedId
                };
              }
              return normalized;
            });
            allEvents.push(...processedEvents);
          }
          
          // Add FILED/RADICADO event if radicado exists (only if not already in events array)
          const hasRadicadoEvent = allEvents.some(e => {
            const eventType = e.eventType || mapEventTypeToEnglish(e.tipoEvento);
            return eventType === "FILED";
          });
          if (rootData?.createdAt && !hasRadicadoEvent) {
            const createdAt = rootData.createdAt?.toDate ? rootData.createdAt.toDate() : rootData.createdAt;
            const now = createdAt instanceof Date ? createdAt : new Date(createdAt);
            const generatedEvent = {
              eventType: "FILED",
              tipoEvento: "RADICADO",
              at: createdAt,
              fecha: createdAt,
              byUserId: rootData.createdByUserId || null,
              porUserId: rootData.createdByUserId || null,
              byName: rootData.createdByNombre || "Sistema",
              porNombre: rootData.createdByNombre || "Sistema",
              documentId: rootData.filedId,
              documentoId: rootData.filedId,
              documentName: `${rootData.filedId}.pdf`,
              documentoNombre: `${rootData.filedId}.pdf`,
              eventId: `FILED:${rootData.filedId}:none:${now.getTime()}`,
              isClickable: isResponseView, // Make clickable if viewing a response
              originalRadicadoReferenceId: rootData.referenceId || originalRadicadoRef,
              originalRadicadoId: rootData.filedId
            };
            allEvents.push(generatedEvent);
          }
          
          // Add ASSIGNED/ASIGNADO event if responder exists (only if not already in events)
          const hasAssignedEvent = allEvents.some(e => {
            const eventType = e.eventType || mapEventTypeToEnglish(e.tipoEvento);
            return eventType === "ASSIGNED" && 
                   (e.toUserId || e.paraUserId) === rootData.response?.respondedorUserId;
          });
          if (rootData?.response?.respondedorUserId && !hasAssignedEvent) {
            const assignDate = rootData.createdAt?.toDate ? rootData.createdAt.toDate() : rootData.createdAt || new Date();
            const now = assignDate instanceof Date ? assignDate : new Date(assignDate);
            const generatedEvent = {
              eventType: "ASSIGNED",
              tipoEvento: "ASIGNADO",
              at: assignDate,
              fecha: assignDate,
              byUserId: rootData.createdByUserId || null,
              porUserId: rootData.createdByUserId || null,
              byName: rootData.createdByNombre || "Sistema",
              porNombre: rootData.createdByNombre || "Sistema",
              toUserId: rootData.response.respondedorUserId,
              paraUserId: rootData.response.respondedorUserId,
              toName: rootData.response.respondedorNombre || "",
              paraNombre: rootData.response.respondedorNombre || "",
              eventId: `ASSIGNED:none:${rootData.response.respondedorUserId}:${now.getTime()}`,
              isClickable: false
            };
            allEvents.push(generatedEvent);
          }
          
          // Query for all response radicados linked to this root
          // Note: Firestore supports nested field queries with dot notation
          // IMPORTANT: Only include radicados that have parentRadicadoId (actual responses, not the original)
          try {
            const responsesQuery = query(
              collection(firestore, "filed"),
              where("response.rootRadicadoId", "==", rootRadicadoId)
            );
            const responsesSnapshot = await getDocs(responsesQuery);
            
            const responseEvents = [];
            responsesSnapshot.forEach((doc) => {
              const responseData = doc.data();
              const responseId = doc.id;
              
              // CRITICAL: Only process if this is an actual response (has parentRadicadoId)
              // Exclude the original radicado itself (which might have rootRadicadoId set to itself)
              if (!responseData || !responseData.response?.parentRadicadoId) {
                return; // Skip this document - it's not a response or data is invalid
              }
              
              // Also exclude if this is the root radicado itself
              if (responseId === rootRadicadoId || responseData.filedId === rootRadicadoId) {
                return; // Skip the original radicado
              }

              // Not every outgoing doc chained to a radicado is an answer: a
              // requerimiento de información and an acto de desistimiento are
              // filed the same way, and calling them "Respuesta" in the
              // trazabilidad misreads the expediente.
              const responseKind = responseData.response?.kind || "response";

              // A cancelled radicado offers no document. One a person annulled
              // is listed as the annulment, opening its annulled page; one the
              // system cancelled (a requerimiento whose render failed) never
              // existed for anyone and stays out.
              if (responseData.status === "CANCELLED") {
                const cancelledEvent = responseCancellationEvent(responseId, responseData, responseKind);
                if (cancelledEvent) responseEvents.push(cancelledEvent);
                return;
              }

              // Get the response document name/URL - prefer fileUrl or filedId.
              // The last URL segment may be percent-encoded (a storage path like
              // "<code>%2F<code>.pdf"), so decode before showing it to a human.
              const decodeSegment = (segment) => {
                try {
                  return decodeURIComponent(segment);
                } catch (_) {
                  return segment;
                }
              };
              const responseDocumentName = responseData.fileUrl
                ? (decodeSegment((responseData.fileUrl.split("?")[0].split("/").pop() || "")).split("/").pop()
                  || `${responseId}.pdf`)
                : (responseData.filedId 
                  ? `${responseData.filedId}.pdf`
                  : `${responseId}.pdf`);
              
              // Add RESPONSE_CREATED/RESPUESTA_CREADA event when response was created
              if (responseData.createdAt) {
                const createdAt = responseData.createdAt?.toDate ? responseData.createdAt.toDate() : responseData.createdAt;
                const now = createdAt instanceof Date ? createdAt : new Date(createdAt);
                responseEvents.push({
                  eventType: "RESPONSE_CREATED",
                  tipoEvento: "RESPUESTA_CREADA",
                  at: createdAt,
                  fecha: createdAt,
                  byUserId: responseData.createdByUserId || null,
                  porUserId: responseData.createdByUserId || null,
                  byName: responseData.createdByNombre || "Sistema",
                  porNombre: responseData.createdByNombre || "Sistema",
                  documentId: responseId,
                  documentoId: responseId,
                  documentName: responseDocumentName,
                  documentoNombre: responseDocumentName,
                  documentoUrl: responseData.fileUrl || null,
                  responseReferenceId: responseData.referenceId,
                  responseFiledId: responseId,
                  responseKind,
                  eventId: `RESPONSE_CREATED:${responseId}:none:${now.getTime()}`,
                  isClickable: true
                });
              }
              
              // Add RESPONSE_FILED/RESPUESTA_RADICADA event if response is finalized
              if (responseData.status === "DONE") {
                const doneAt = responseData.doneAt?.toDate ? responseData.doneAt.toDate() : responseData.doneAt || responseData.createdAt?.toDate ? responseData.createdAt.toDate() : responseData.createdAt;
                const now = doneAt instanceof Date ? doneAt : new Date(doneAt);
                responseEvents.push({
                  eventType: "RESPONSE_FILED",
                  tipoEvento: "RESPUESTA_RADICADA",
                  at: doneAt,
                  fecha: doneAt,
                  byUserId: responseData.doneByUserId || null,
                  porUserId: responseData.doneByUserId || null,
                  byName: responseData.doneByNombre || "Sistema",
                  porNombre: responseData.doneByNombre || "Sistema",
                  documentId: responseId,
                  documentoId: responseId,
                  documentName: responseDocumentName,
                  documentoNombre: responseDocumentName,
                  documentoUrl: responseData.fileUrl || null,
                  responseReferenceId: responseData.referenceId,
                  responseFiledId: responseId,
                  responseKind,
                  eventId: `RESPONSE_FILED:${responseId}:none:${now.getTime()}`,
                  isClickable: true
                });
              }
            });
            
            // Add response events to timeline
            allEvents.push(...responseEvents);
          } catch (queryError) {
            console.error("Error querying responses:", queryError);
            // If nested query fails, try alternative approach
            // The backend might need to index this field or we might need a different query structure
          }
          
          // Add FINALIZED/FINALIZADO event if root status is DONE
          const hasFinalizedEvent = allEvents.some(e => {
            const eventType = e.eventType || mapEventTypeToEnglish(e.tipoEvento);
            return eventType === "FINALIZED";
          });
          if ((rootData?.status === "DONE" || wasFinalizedBeforeCancelling(rootData)) && !hasFinalizedEvent) {
            const doneDate = rootData.doneAt?.toDate ? rootData.doneAt.toDate() : rootData.doneAt || rootData.createdAt?.toDate ? rootData.createdAt.toDate() : rootData.createdAt;
            const now = doneDate instanceof Date ? doneDate : new Date(doneDate);
            const generatedEvent = {
              eventType: "FINALIZED",
              tipoEvento: "FINALIZADO",
              at: doneDate,
              fecha: doneDate,
              byUserId: rootData.doneByUserId || null,
              porUserId: rootData.doneByUserId || null,
              byName: rootData.doneByNombre || "Sistema",
              porNombre: rootData.doneByNombre || "Sistema",
              eventId: `FINALIZED:none:none:${now.getTime()}`,
              isClickable: false
            };
            allEvents.push(generatedEvent);
          }
          
          // The annulment of the root radicado itself. A PQRSD already logged
          // it on events[]; that entry is swapped for one carrying the email
          // outcome rather than listed twice.
          const rootCancelled = rootCancellationEvent(
            rootData,
            allEvents.map((e) => ({ ...e, eventType: e.eventType || mapEventTypeToEnglish(e.tipoEvento) }))
          );
          if (rootCancelled) {
            const loggedIndex = allEvents.findIndex(
              (e) => (e.eventType || mapEventTypeToEnglish(e.tipoEvento)) === "PQRSD_CANCELLED"
            );
            if (loggedIndex >= 0) allEvents.splice(loggedIndex, 1, rootCancelled);
            else allEvents.push(rootCancelled);
          }

          // Deduplicate events by eventId (deterministic)
          const uniqueEvents = [];
          const seenEventIds = new Set();
          
          // Normalize all events first
          const normalizedEvents = allEvents.map(e => normalizeEvent(e));
          
          // Dedupe by eventId
          normalizedEvents.forEach((event) => {
            const eventId = event.eventId || generateEventIdFromEvent(event);
            if (!seenEventIds.has(eventId)) {
              seenEventIds.add(eventId);
              uniqueEvents.push(event);
            }
          });
          
          // Sort all events by date (use at or fecha). An annulment recorded
          // without a date (before the motive was required) can only have come
          // last, so it goes after everything dated instead of first.
          const isUndated = (e) => !e.at && !e.fecha;
          const undatedTail = uniqueEvents.filter(
            (e) => isUndated(e) && CANCELLATION_EVENT_TYPES.includes(e.eventType || mapEventTypeToEnglish(e.tipoEvento))
          );
          const sortedEvents = uniqueEvents.filter((e) => !undatedTail.includes(e)).sort((a, b) => {
            const dateA = a.at?.toDate ? a.at.toDate() : (a.fecha?.toDate ? a.fecha.toDate() : (a.at ? new Date(a.at) : (a.fecha ? new Date(a.fecha) : new Date(0))));
            const dateB = b.at?.toDate ? b.at.toDate() : (b.fecha?.toDate ? b.fecha.toDate() : (b.at ? new Date(b.at) : (b.fecha ? new Date(b.fecha) : new Date(0))));
            return dateA - dateB;
          });
          sortedEvents.push(...undatedTail);
          
          // Group response events together
          // Find pairs of RESPONSE_CREATED/RESPUESTA_CREADA and RESPONSE_FILED/RESPUESTA_RADICADA with same documentId
          const groupedEvents = [];
          const processedRadicadas = new Set();
          const processedCreadas = new Set();
          
          sortedEvents.forEach((event, index) => {
            const eventType = event.eventType || mapEventTypeToEnglish(event.tipoEvento);
            const documentId = event.documentId || event.documentoId;

            // If this is a RESPONSE_CREATED, check if there's a matching RESPONSE_FILED
            if (eventType === "RESPONSE_CREATED" && documentId && !processedCreadas.has(documentId)) {
              // Find matching RESPONSE_FILED
              const matchingRadicada = sortedEvents.find(
                (e, idx) => {
                  const eType = e.eventType || mapEventTypeToEnglish(e.tipoEvento);
                  const eDocId = e.documentId || e.documentoId;
                  return idx > index &&
                         eType === "RESPONSE_FILED" &&
                         eDocId === documentId &&
                         !processedRadicadas.has(eDocId);
                }
              );
              
              if (matchingRadicada) {
                // Mark both as processed
                processedCreadas.add(documentId);
                processedRadicadas.add(matchingRadicada.documentId || matchingRadicada.documentoId);
                // Add RESPONSE_CREATED as parent
                groupedEvents.push({
                  ...event,
                  isParent: true,
                  hasChild: true
                });
                // Add RESPONSE_FILED as child (right after parent)
                groupedEvents.push({
                  ...matchingRadicada,
                  isChild: true,
                  parentDocumentoId: documentId
                });
              } else {
                // No matching radicada, add as regular event
                processedCreadas.add(documentId);
                groupedEvents.push(event);
              }
            } else if (eventType === "RESPONSE_FILED" && documentId) {
              // Only add if not already processed (as part of a pair)
              if (!processedRadicadas.has(documentId)) {
                processedRadicadas.add(documentId);
                groupedEvents.push(event);
              }
            } else if (eventType !== "RESPONSE_CREATED" && eventType !== "RESPONSE_FILED") {
              // Regular event, add as is
              groupedEvents.push(event);
            }
          });
          
          setTimeline(groupedEvents);
        } catch (error) {
          console.error("Error loading timeline:", error);
          setTimeline([]);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setLoading(false);
    }
  }, [rootRadicadoId, token, isResponseView, originalRadicadoRef, publicFiled]);

  if (loading) {
    return (
      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: "1.1rem", sm: "1.25rem" }
          }}
        >
          Trazabilidad del radicado
        </Typography>
        <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
          >
            Cargando...
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  // Show empty state if no events yet
  if (timeline.length === 0) {
    return (
      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: "1.1rem", sm: "1.25rem" }
          }}
        >
          Trazabilidad del radicado
        </Typography>
        <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
          >
            No hay eventos de trazabilidad aún
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
      <Typography 
        variant="h6" 
        sx={{ 
          mb: { xs: 1.5, sm: 2 },
          fontSize: { xs: "1.1rem", sm: "1.25rem" }
        }}
      >
        Trazabilidad del radicado
      </Typography>
      <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ position: "relative" }}>
          {timeline.map((evento, index) => {
            const handleClick = () => {
              if (evento.isClickable) {
                const eventType = evento.eventType || mapEventTypeToEnglish(evento.tipoEvento);
                // An annulled response opens its annulled page: it has no document.
                if (evento.cancelledFiledId) {
                  navigate(`/filed/cancelled/${encodeURIComponent(evento.cancelledFiledId)}`);
                }
                // Handle navigation to original radicado (FILED/RADICADO event in response view)
                else if (eventType === "FILED" && evento.originalRadicadoReferenceId) {
                  navigate(`/filed/view/${evento.originalRadicadoReferenceId}`);
                }
                // Handle navigation to response radicado
                else if (evento.responseReferenceId) {
                  navigate(`/filed/view/${evento.responseReferenceId}`);
                } else if (evento.responseFiledId) {
                  navigate(`/filed/${evento.responseFiledId}`);
                }
              }
            };
            
            const isLast = index === timeline.length - 1;
            const isChild = evento.isChild;
            const isParent = evento.isParent;
            const nextEvent = timeline[index + 1];
            const isNextChild = nextEvent?.isChild;
            
            // Check if this is the current radicado being viewed
            const documentId = evento.documentId || evento.documentoId;
            const isCurrentRadicado = 
              (currentRadicadoId && documentId === currentRadicadoId) ||
              (currentRadicadoReferenceId && evento.responseReferenceId === currentRadicadoReferenceId);
            
            const eventType = evento.eventType || mapEventTypeToEnglish(evento.tipoEvento);
            const isRespuestaRadicada = eventType === "RESPONSE_FILED";

            // Calculate marker column width based on child status and screen size
            const markerColumnWidth = isChild 
              ? { xs: 28, sm: 40 } 
              : { xs: 24, sm: 32 };
            
            return (
              <Box 
                key={index}
                sx={{ 
                  display: "flex",
                  position: "relative",
                  pb: isLast && !isChild ? 0 : (isRespuestaRadicada ? { xs: 2.5, sm: 3.5 } : { xs: 2, sm: 2.5 }),
                  minHeight: isRespuestaRadicada ? { xs: "60px", sm: "70px" } : { xs: "50px", sm: "60px" }
                }}
              >
                {/* Left column: Marker and line */}
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: markerColumnWidth.xs + "px", sm: markerColumnWidth.sm + "px" },
                    flexShrink: 0
                  }}
                >
                  {/* Horizontal branch line for children */}
                  {isChild && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: { xs: "10px", sm: "15px" },
                        top: { xs: "22px", sm: "28px" },
                        width: { xs: "16px", sm: "24px" },
                        height: "2px",
                        backgroundColor: "#e0e0e0",
                        zIndex: 0
                      }}
                    />
                  )}
                  
                  {/* Vertical line - adjust based on whether it's a child or parent */}
                  {!isLast && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: { xs: "10px", sm: "15px" },
                        top: { xs: "22px", sm: "28px" },
                        bottom: isNextChild ? "-10px" : "-10px",
                        width: "2px",
                        backgroundColor: "#e0e0e0",
                        zIndex: 0
                      }}
                    />
                  )}
                  
                  {/* Additional vertical line for parent when it has a child */}
                  {isParent && evento.hasChild && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: { xs: "10px", sm: "15px" },
                        top: { xs: "22px", sm: "28px" },
                        bottom: "-10px",
                        width: "2px",
                        backgroundColor: "#e0e0e0",
                        zIndex: 0
                      }}
                    />
                  )}
                  
                  {/* Event circle/dot */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: { xs: "4px", sm: "8px" },
                      top: { xs: "4px", sm: "8px" },
                      width: { xs: "12px", sm: "16px" },
                      height: { xs: "12px", sm: "16px" },
                      borderRadius: "50%",
                      backgroundColor: evento.isClickable || isCurrentRadicado ? "primary.main" : "#9e9e9e",
                      border: "2px solid white",
                      zIndex: 2,
                      boxShadow: "0 0 0 2px " + (evento.isClickable || isCurrentRadicado ? "rgba(25, 118, 210, 0.2)" : "rgba(158, 158, 158, 0.2)")
                    }}
                  />
                </Box>
                
                {/* Right column: Content */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    ...(isCurrentRadicado ? {
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                      borderRadius: 2,
                      p: { xs: 1, sm: 1.5 },
                      border: "2px solid",
                      borderColor: "primary.main"
                    } : {})
                  }}
                >
                  {/* Event content */}
                  <Box 
                    component={evento.isClickable && !isCurrentRadicado ? "button" : "div"}
                    sx={{ 
                      display: "flex", 
                      flexDirection: "column",
                      position: "relative",
                      zIndex: 1,
                      pt: { xs: 0, sm: 0.5 },
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      background: "none",
                      padding: evento.isClickable && !isCurrentRadicado 
                        ? { xs: "8px", sm: "8px" }
                        : { xs: 0, sm: 0 },
                      ...(evento.isClickable && !isCurrentRadicado ? {
                        cursor: "pointer",
                        touchAction: "manipulation",
                        WebkitTapHighlightColor: "rgba(25, 118, 210, 0.1)",
                        minHeight: { xs: "44px", sm: "auto" }, // Minimum touch target
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.04)",
                          borderRadius: 1
                        },
                        "&:active": {
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                        },
                        ml: { xs: -0.5, sm: -1 },
                        mr: { xs: -0.5, sm: -1 },
                        mt: { xs: -0.25, sm: -0.5 }
                      } : {})
                    }}
                    onClick={evento.isClickable && !isCurrentRadicado ? handleClick : undefined}
                  >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: isCurrentRadicado ? "bold" : "medium",
                      ...(evento.isClickable || isCurrentRadicado ? { color: "primary.main" } : {}),
                      fontSize: isCurrentRadicado 
                        ? { xs: "0.95rem", sm: "1.1rem" }
                        : { xs: "0.875rem", sm: "1rem" }
                    }}
                  >
                    {getEventLabel(evento)}
                    {evento.isClickable && !isCurrentRadicado && <span> →</span>}
                    {isCurrentRadicado && <span> (Actual)</span>}
                    {eventType === "FILED" && evento.isClickable && !isCurrentRadicado && <span> (Original)</span>}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {formatSpanishDate(
                      evento.at?.toDate ? evento.at.toDate() : (evento.fecha?.toDate ? evento.fecha.toDate() : (evento.at ? new Date(evento.at) : (evento.fecha ? new Date(evento.fecha) : null)))
                    )}
                  </Typography>
                  {(evento.porNombre || evento.byName) && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ mt: { xs: 0.25, sm: 0.5 }, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                    >
                      Por: {evento.porNombre || evento.byName}
                    </Typography>
                  )}
                  {CANCELLATION_EVENT_TYPES.includes(eventType) && (
                    <>
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: { xs: 0.25, sm: 0.5 }, fontSize: { xs: "0.7rem", sm: "0.75rem" }, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        Motivo: {evento.reason || "no registrado"}
                      </Typography>
                      {evento.notification && (
                        <Typography
                          variant="caption"
                          color={evento.notification.severity === "warning" ? "warning.dark" : "text.secondary"}
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" }, wordBreak: "break-word" }}
                        >
                          {evento.notification.text}
                        </Typography>
                      )}
                    </>
                  )}
                  {(evento.documentoNombre || evento.documentName || eventType === "RESPONSE_CREATED" || eventType === "RESPONSE_FILED") && (
                    <Typography 
                      variant="caption" 
                      color={evento.isClickable || isCurrentRadicado ? "primary" : "text.secondary"}
                      sx={{ 
                        mt: { xs: 0.25, sm: 0.5 },
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        wordBreak: "break-word",
                        ...(evento.isClickable && !isCurrentRadicado ? { 
                          textDecoration: "underline",
                          fontWeight: { xs: "500", sm: "400" }, // Slightly bolder on mobile for visibility
                          display: "block",
                          minHeight: { xs: "32px", sm: "auto" } // Better touch target for document link
                        } : {})
                      }}
                    >
                      Documento: {evento.documentoNombre || evento.documentName || "(No disponible)"}
                    </Typography>
                  )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}
